import { A4, palette, pt } from './print';

/**
 * The look of what is printed, as the canvas draws it: the two style sheets of the renderer
 * (`blocks.tsx` and `DesignReport.tsx` under `backend/src/core/utils/report/pdf`) written
 * again for the browser, name for name and number for number, each length in PDF points
 * through `pt`. A size changed there is changed here. Only what stands under the last heading
 * is chosen here: what the canvas draws and a report does not.
 *
 * What the two engines do differently is said once: the renderer lays every box out as a
 * column and lets a text shrink below its content, which a browser does only when told
 * (`column`, `minWidth: 0`).
 */

const SEMIBOLD = 600;

const column = { display: 'flex', flexDirection: 'column' } as const;

/** A box of the renderer with no style of its own. Margins inside it add up, where a browser's block would fold them. */
export const viewStyles = column;
const row = { display: 'flex', flexDirection: 'row' } as const;
const hairlineEdge = `${pt(0.75)} solid ${palette.hairline}`;

/**
 * A sheet's own type: the renderer's one font, which is this interface's as well, and the
 * line height that font has there (its ascent, descent and line gap come to one and a half).
 */
export const typeStyles = {
  fontFamily: 'Poppins, sans-serif',
  lineHeight: 1.5,
  color: palette.ink,
  // A sheet is paper whatever the interface's own colours are.
  colorScheme: 'light',
  textAlign: 'left',
};

/** A cover or a closing page (`freePage`): its margins, and its blocks one under the other. */
export const freePage = { paddingY: 46, paddingX: 52 } as const;

/** The width a block has on a cover or a closing page, in points: what the overlay of a dragged one is scaled by. */
export const BLOCK_WIDTH = A4.width - 2 * freePage.paddingX;

/** A page of results (`page`): its margins and its text size, laid over the sheet that stands for it. */
export const flowPageStyles = {
  position: 'absolute',
  inset: 0,
  fontSize: pt(9),
  padding: `${pt(58)} ${pt(42)}`,
};

// ---- blocks.tsx ----

export const blockStyles = {
  hero: { ...column, marginBottom: pt(12) },
  heroOverline: { fontSize: pt(9), letterSpacing: pt(3), marginBottom: pt(10) },
  heroTitle: { fontSize: pt(24), fontWeight: SEMIBOLD, marginBottom: pt(4) },
  heroSubtitle: { fontSize: pt(12), marginBottom: pt(16) },
  heroRule: { width: pt(48), height: pt(3), borderRadius: pt(1.5), marginBottom: pt(8) },
  heading1: { fontSize: pt(15), fontWeight: SEMIBOLD, marginBottom: pt(10), marginTop: pt(4) },
  heading2: {
    fontSize: pt(8),
    fontWeight: SEMIBOLD,
    letterSpacing: pt(1.4),
    borderBottom: `${pt(1)} solid`,
    paddingBottom: pt(3),
    marginBottom: pt(8),
    marginTop: pt(6),
  },
  // The renderer breaks a line where the author did.
  text: { marginBottom: pt(8), whiteSpace: 'pre-wrap', overflowWrap: 'break-word' },
  fields: { ...row, flexWrap: 'wrap', marginBottom: pt(8) },
  field: { marginBottom: pt(8), paddingRight: pt(12), minWidth: 0 },
  fieldLabel: { fontSize: pt(6.5), letterSpacing: pt(1), marginBottom: pt(1) },
  fieldValue: { fontSize: pt(10.5), fontWeight: SEMIBOLD, overflowWrap: 'break-word' },
  imageBox: { ...column, marginBottom: pt(8) },
  image: { display: 'block', width: '100%', maxHeight: pt(300), objectFit: 'contain' },
  imageFramed: { borderRadius: pt(4) },
  frame: { backgroundColor: palette.paper, borderRadius: pt(6), padding: pt(6) },
  frameEdge: { border: hairlineEdge },
  photoGrid: { ...row, flexWrap: 'wrap', justifyContent: 'space-between' },
  photoCell: { marginBottom: pt(10) },
  photo: { display: 'block', width: '100%', objectFit: 'cover', borderRadius: pt(3), border: hairlineEdge },
  photoCaption: { fontSize: pt(7), marginTop: pt(2) },
  // From the bottom, so the lines to sign on are level whatever the height of their labels.
  signatures: { ...row, alignItems: 'flex-end', marginTop: pt(14), marginBottom: pt(8) },
  signature: { flex: 1, minWidth: 0, marginRight: pt(14) },
  signatureLabel: { fontSize: pt(6.5), letterSpacing: pt(1), marginBottom: pt(26) },
  signatureLine: { borderTop: `${pt(1)} solid`, paddingTop: pt(3) },
  signatureCaption: { fontSize: pt(7) },
  divider: { borderBottom: `${pt(1)} solid`, marginTop: pt(8), marginBottom: pt(8) },
};

/** A photo's share of its row and its height, by the number of columns in the grid (`PHOTO_CELLS`). */
export const PHOTO_CELLS: Record<number, { width: string; height: number }> = {
  1: { width: '100%', height: 300 },
  2: { width: '49%', height: 168 },
  3: { width: '32%', height: 110 },
};

export const FLEX_ALIGN = { left: 'flex-start', center: 'center', right: 'flex-end' } as const;

// ---- DesignReport.tsx ----

// Where on its page a strip stands is the sheet's to say (`stripPlaceStyles`): there it is a button too.
const chromeStrip = { ...row, alignItems: 'center', justifyContent: 'space-between' };

export const reportStyles = {
  header: { ...chromeStrip, borderBottom: `${pt(1)} solid ${palette.hairline}`, paddingBottom: pt(5) },
  footer: { ...chromeStrip, borderTop: `${pt(1)} solid ${palette.hairline}`, paddingTop: pt(5) },
  chromeSlot: { ...row, alignItems: 'center', flex: 1, minWidth: 0 },
  chromeText: { fontSize: pt(7), color: palette.faint },
  chromeBrand: { fontSize: pt(7.5), fontWeight: SEMIBOLD, color: palette.muted },
  // The height alone: an uploaded logo is rarely square, and its width follows its shape.
  chromeLogo: { display: 'block', height: pt(13) },
  sectionTitle: {
    fontSize: pt(8),
    fontWeight: SEMIBOLD,
    letterSpacing: pt(1.4),
    color: palette.muted,
    borderBottom: `${pt(1)} solid ${palette.hairline}`,
    paddingBottom: pt(3),
    marginBottom: pt(8),
    marginTop: pt(6),
  },
  verdictPanel: { borderRadius: pt(5), padding: pt(10), marginBottom: pt(10) },
  verdictText: { fontSize: pt(10), fontWeight: SEMIBOLD },
  completionRow: { ...row, alignItems: 'center', marginBottom: pt(8) },
  completionNumber: { fontSize: pt(26), fontWeight: SEMIBOLD, marginRight: pt(3) },
  completionUnit: { fontSize: pt(13), fontWeight: SEMIBOLD, color: palette.muted, marginRight: pt(12) },
  completionFigure: { fontSize: pt(16), fontWeight: SEMIBOLD, marginRight: pt(15) },
  progressTrack: {
    ...row, height: pt(8), borderRadius: pt(4), overflow: 'hidden', backgroundColor: palette.panel, flex: 1,
  },
  legend: { ...row, flexWrap: 'wrap', marginBottom: pt(10) },
  legendItem: { ...row, alignItems: 'center', marginRight: pt(12), marginBottom: pt(2) },
  legendDot: { width: pt(6), height: pt(6), borderRadius: pt(3), marginRight: pt(4) },
  legendText: { fontSize: pt(7.5), color: palette.muted },
  tableHeader: { ...row, borderBottom: `${pt(1)} solid ${palette.ink}`, paddingBottom: pt(3), marginBottom: pt(1) },
  tableHeaderText: { fontSize: pt(6.5), letterSpacing: pt(1), color: palette.muted },
  tableRow: {
    ...row, alignItems: 'center', borderBottom: `${pt(1)} solid ${palette.hairline}`, paddingTop: pt(4), paddingBottom: pt(4),
  },
  tableEnd: { height: pt(10) },
  cellText: { fontSize: pt(8.5) },
  cellMuted: { fontSize: pt(8), color: palette.faint },
  summaryColumn: { width: '20%' },
  numberColumn: { width: '8%' },
  descriptionColumn: { width: '30%' },
  statusColumn: { width: '20%' },
  dateColumn: { width: '16%' },
  cellGap: { paddingRight: pt(6) },
  pill: { borderRadius: pt(7), padding: `${pt(1.5)} ${pt(6)}`, alignSelf: 'flex-start', flexShrink: 0 },
  pillText: { fontSize: pt(7), fontWeight: SEMIBOLD, whiteSpace: 'nowrap' },
  cardGap: { height: pt(12) },
  card: { ...column, borderLeft: `${pt(2.5)} solid`, borderRadius: pt(2), paddingLeft: pt(8), paddingTop: pt(4) },
  cardHeader: { ...row, alignItems: 'center', marginBottom: pt(2) },
  cardNumber: { fontSize: pt(8), color: palette.faint, width: pt(20), flexShrink: 0 },
  cardName: { fontSize: pt(9.5), fontWeight: SEMIBOLD, flex: 1, minWidth: 0, paddingRight: pt(6) },
  stepRow: { ...row, marginTop: pt(3), paddingLeft: pt(20) },
  stepNumberColumn: { width: pt(22), flexShrink: 0 },
  stepNumber: { fontSize: pt(7.5), color: palette.faint },
  stepBody: { flex: 1, minWidth: 0, paddingRight: pt(8) },
  stepText: { fontSize: pt(8.5) },
  stepComment: { fontSize: pt(8), fontStyle: 'italic', color: palette.muted, marginTop: pt(1) },
  stepDotColumn: { width: pt(7), flexShrink: 0 },
  stepDot: { width: pt(7), height: pt(7), borderRadius: pt(3.5), marginTop: pt(2.5) },
  // Under the step's text: past the card's indent and the step number.
  stepAnswers: { paddingLeft: pt(42), paddingRight: pt(15) },
  thumbRow: { ...row, flexWrap: 'wrap', gap: pt(5), paddingLeft: pt(20), marginTop: pt(4) },
  thumbRowFollowed: { marginBottom: pt(5) },
  thumb: { display: 'block', width: pt(94), height: pt(70), objectFit: 'cover', borderRadius: pt(2), border: hairlineEdge },
  gridRow: { ...row, justifyContent: 'space-between' },
  gridGap: { height: pt(8) },
  gridCard: {
    ...column,
    width: '48.5%',
    border: `${pt(1)} solid ${palette.hairline}`,
    borderLeft: `${pt(3)} solid`,
    borderRadius: pt(4),
    padding: pt(8),
  },
  detailHeader: { ...row, alignItems: 'center', marginBottom: pt(10) },
  detailTitle: { fontSize: pt(14), fontWeight: SEMIBOLD, flex: 1, minWidth: 0, paddingRight: pt(8) },
  // A step's answers and photos end it; the next one starts clear of them.
  detailStep: { marginTop: pt(10) },
  detailRow: { ...row, marginBottom: pt(3) },
  detailLabelColumn: { width: pt(80), flexShrink: 0 },
  detailLabel: { fontSize: pt(8), color: palette.muted },
  detailValue: { fontSize: pt(9), flex: 1, minWidth: 0 },
  detailPhoto: {
    display: 'block', width: '100%', maxHeight: pt(220), objectFit: 'contain', borderRadius: pt(4), border: hairlineEdge, marginTop: pt(8),
  },
  detailPhotoCaption: { fontSize: pt(7), color: palette.faint, marginTop: pt(2), textAlign: 'center' },
};

/**
 * The answers to a step's POI type, which the canvas draws as one line where the report
 * prints a table of them (`CustomFields.tsx`, `compact` in a card and `regular` on a page of
 * its own): the type's name as that table's heading, then each label with its answer.
 */
export const answerStyles = {
  compact: { marginTop: pt(5), fontSize: pt(8), color: '#444444' },
  regular: { marginTop: pt(14), fontSize: pt(11), color: '#444444' },
  name: { fontWeight: SEMIBOLD },
  value: { color: palette.ink },
  mark: { display: 'inline-block', width: '0.9em', height: '0.9em', verticalAlign: '-0.1em' },
};

// ---- What only the canvas draws ----

/**
 * A block that prints nothing as it stands (a text nobody has written, an image nobody has
 * chosen), or one of a type this version cannot draw. The report leaves it out; the canvas
 * has to show it, or it could be neither selected nor moved nor deleted.
 */
export const hollowBlockStyles = {
  ...row,
  alignItems: 'center',
  gap: pt(6),
  minHeight: pt(28),
  padding: `${pt(6)} ${pt(8)}`,
  marginBottom: pt(8),
  borderRadius: pt(3),
  border: `${pt(1)} dashed`,
  fontSize: pt(8),
  '& i': { fontSize: pt(11) },
};

/** A section that is not printed: a quiet line in its place, so that it can still be selected. */
export const unprintedStyles = {
  padding: `${pt(6)} 0`,
  fontSize: pt(8),
  fontStyle: 'italic',
  color: palette.faint,
};

/** Between the summary and the one checkpoint a `detailed` design is shown by: the page breaks here. */
export const pageBreakStyles = {
  margin: `${pt(14)} 0 ${pt(12)}`,
  paddingTop: pt(4),
  borderTop: `${pt(1)} dashed ${palette.hairline}`,
  fontSize: pt(7),
  color: palette.faint,
  textAlign: 'center',
};
